'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef, Suspense } from 'react';
function ScrollRestorationInner({
  debug = false
}: {
  debug?: boolean;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const STORAGE_KEY = 'scroll-positions';
  const ROUTE_STORAGE_KEY = 'scroll-positions-by-route';
  const currentScrollRef = useRef(0);
  const isScrollingProgrammaticallyRef = useRef(false);
  const seenHistoryIndicesRef = useRef(new Set<number>());
  const isInitialLoadRef = useRef(true);
  const log = (...args: any[]) => {
    if (debug) {
      console.log('[ScrollRestoration]', ...args);
    }
  };
  const getRouteKey = (path: string, search: string) => {
    return path + (search ? `?${search}` : '');
  };
  const isPageReload = () => {
    try {
      const entries = performance.getEntriesByType('navigation');
      if (entries.length > 0) {
        return (entries[0] as PerformanceNavigationTiming).type === 'reload';
      }
    } catch {}
    return false;
  };
  const getScrollPositions = (): Record<string, number> => {
    if (typeof window === 'undefined') return {};
    try {
      const stored = window.sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };
  const getRouteScrollPositions = (): Record<string, number> => {
    if (typeof window === 'undefined') return {};
    try {
      const stored_0 = window.sessionStorage.getItem(ROUTE_STORAGE_KEY);
      return stored_0 ? JSON.parse(stored_0) : {};
    } catch {
      return {};
    }
  };
  const saveScrollPosition = (key: string, position: number) => {
    try {
      const positions = getScrollPositions();
      positions[key] = position;
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
      log('Saved:', key, position);
    } catch {}
  };
  const saveRouteScrollPosition = (routeKey: string, position_0: number) => {
    try {
      const positions_0 = getRouteScrollPositions();
      positions_0[routeKey] = position_0;
      sessionStorage.setItem(ROUTE_STORAGE_KEY, JSON.stringify(positions_0));
      log('Saved route position:', routeKey, position_0);
    } catch {}
  };

  // Initialize history state
  useEffect(() => {
    if (window.history.state?.scrollHistoryIndex === undefined) {
      const index = Date.now();
      window.history.replaceState({
        ...window.history.state,
        scrollHistoryIndex: index
      }, '');
      seenHistoryIndicesRef.current.add(index);
      log('Initialized history index:', index);
    } else {
      const index_0 = window.history.state.scrollHistoryIndex;
      seenHistoryIndicesRef.current.add(index_0);
      log('Found existing history index:', index_0);
    }
  }, []);
  useLayoutEffect(() => {
    const routeKey_0 = getRouteKey(pathname, searchParams.toString());
    const currentHistoryIndex = window.history.state?.scrollHistoryIndex;

    // Detect page reload on initial load
    const isReload = isInitialLoadRef.current && isPageReload();

    // If we've seen this history index before, it's back/forward navigation
    const isBackForward = !isReload && currentHistoryIndex !== undefined && seenHistoryIndicesRef.current.has(currentHistoryIndex);
    log('LayoutEffect - route:', routeKey_0, 'currentIdx:', currentHistoryIndex, 'seenBefore:', isBackForward, 'isReload:', isReload, 'allSeen:', Array.from(seenHistoryIndicesRef.current));

    // Create unique key using route + history index
    const historyRouteKey = currentHistoryIndex ? `${routeKey_0}::${currentHistoryIndex}` : routeKey_0;
    isScrollingProgrammaticallyRef.current = true;
    if (isReload) {
      // Page reload - restore scroll position using route-level key
      const routePositions = getRouteScrollPositions();
      const savedPosition = routePositions[routeKey_0];
      log('Reload - restoring position:', savedPosition, 'for route:', routeKey_0);
      if (savedPosition !== undefined && savedPosition > 0) {
        window.scrollTo(0, savedPosition);
        // Retry restoration after content may have loaded
        setTimeout(() => {
          window.scrollTo(0, savedPosition);
          setTimeout(() => {
            window.scrollTo(0, savedPosition);
            isScrollingProgrammaticallyRef.current = false;
            currentScrollRef.current = savedPosition;
          }, 150);
        }, 50);
      } else {
        isScrollingProgrammaticallyRef.current = false;
        currentScrollRef.current = 0;
      }

      // Mark as no longer initial load and set up history index
      isInitialLoadRef.current = false;
      const newIndex = Date.now();
      seenHistoryIndicesRef.current.add(newIndex);
      window.history.replaceState({
        ...window.history.state,
        scrollHistoryIndex: newIndex
      }, '');
    } else if (isBackForward) {
      // Back/forward navigation - restore scroll position
      const positions_1 = getScrollPositions();
      const savedPosition_0 = positions_1[historyRouteKey];
      log('Back/forward - restoring position:', savedPosition_0, 'for key:', historyRouteKey);
      if (savedPosition_0 !== undefined) {
        window.scrollTo(0, savedPosition_0);
        setTimeout(() => {
          window.scrollTo(0, savedPosition_0);
          setTimeout(() => {
            isScrollingProgrammaticallyRef.current = false;
            currentScrollRef.current = savedPosition_0;
          }, 100);
        }, 50);
      } else {
        window.scrollTo(0, 0);
        setTimeout(() => {
          isScrollingProgrammaticallyRef.current = false;
          currentScrollRef.current = 0;
        }, 100);
      }
      isInitialLoadRef.current = false;
    } else {
      // Normal navigation (link click, router.push) - scroll to top and create new history entry
      log('Normal navigation - scrolling to top, creating new history index');
      window.scrollTo(0, 0);
      const newIndex_0 = Date.now();
      seenHistoryIndicesRef.current.add(newIndex_0);
      window.history.replaceState({
        ...window.history.state,
        scrollHistoryIndex: newIndex_0
      }, '');
      setTimeout(() => {
        isScrollingProgrammaticallyRef.current = false;
        currentScrollRef.current = 0;
      }, 100);
      isInitialLoadRef.current = false;
    }
  }, [pathname, searchParams]);
  useEffect(() => {
    const routeKey_1 = getRouteKey(pathname, searchParams.toString());
    const currentHistoryIndex_0 = window.history.state?.scrollHistoryIndex;
    const historyRouteKey_0 = currentHistoryIndex_0 ? `${routeKey_1}::${currentHistoryIndex_0}` : routeKey_1;
    log('Effect mounted for route:', routeKey_1, 'historyKey:', historyRouteKey_0, 'currentScroll:', currentScrollRef.current);
    let rafId: number;
    const handleScroll = () => {
      if (isScrollingProgrammaticallyRef.current) {
        log('Ignoring scroll event - programmatic scroll');
        return;
      }
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const scrollPos = window.scrollY;
        currentScrollRef.current = scrollPos;
        saveScrollPosition(historyRouteKey_0, scrollPos);
        saveRouteScrollPosition(routeKey_1, scrollPos);
        rafId = 0;
      });
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      log('Cleanup - saving route:', historyRouteKey_0, 'position:', currentScrollRef.current);
      saveScrollPosition(historyRouteKey_0, currentScrollRef.current);
      saveRouteScrollPosition(routeKey_1, currentScrollRef.current);
    };
  }, [pathname, searchParams]);
  return null;
}
export default function ScrollRestoration(t0) {
  const $ = _c(2);
  const {
    debug: t1,
    disabled: t2
  } = t0;
  const debug = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  if (disabled) {
    return null;
  }
  let t3;
  if ($[0] !== debug) {
    t3 = <Suspense fallback={null}><ScrollRestorationInner debug={debug} /></Suspense>;
    $[0] = debug;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  return t3;
}